import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'motion/react';
import { collection, query, where, getDocs, orderBy } from 'firebase/firestore';
import { MapPin, Clock, Phone, Star, Calendar, Users, Instagram, Facebook, Globe } from 'lucide-react';
import { ImageWithFallback } from '../components/ImageWithFallback';
import { Input } from '../components/ui/Input';
import { db } from '../firebase/config';
import { mockDishes } from '../services/mockData';
import { safeFirebaseQuery } from '../utils/firebaseHelper';
import DishCard from '../components/DishCard';
import './RestaurantProfile.css';

function RestaurantProfile({ user }) {
  const { restaurantName } = useParams();
  const navigate = useNavigate();
  const [restaurant, setRestaurant] = useState(null);
  const [dishes, setDishes] = useState([]);
  const [trendingDishes, setTrendingDishes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [reservationData, setReservationData] = useState({
    date: '',
    time: '',
    guests: '2',
    name: '',
    email: '',
    phone: '',
    specialRequest: '',
  });
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [selectedDate, setSelectedDate] = useState('');
  const [availableSlots, setAvailableSlots] = useState([]);

  const fetchRestaurantData = useCallback(async () => {
    try {
      setLoading(true);
      const decodedName = decodeURIComponent(restaurantName);

      const dishesData = await safeFirebaseQuery(
        async () => {
          const q = query(
            collection(db, 'dishes'),
            where('restaurantName', '==', decodedName),
            orderBy('averageRating', 'desc')
          );
          const querySnapshot = await getDocs(q);
          return querySnapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
          }));
        },
        async () => {
          return mockDishes.filter(d => d.restaurantName === decodedName);
        },
        'Error fetching restaurant dishes'
      );

      if (dishesData.length === 0) {
        navigate('/');
        return;
      }

      // Calculate top 10% threshold for trending dishes
      const allDishes = await safeFirebaseQuery(
        async () => {
          const q = query(collection(db, 'dishes'), orderBy('averageRating', 'desc'));
          const snapshot = await getDocs(q);
          return snapshot.docs.map(doc => doc.data());
        },
        async () => mockDishes,
        'Error fetching all dishes'
      ).catch(() => mockDishes);

      const sortedByRating = allDishes.filter(d => (d.averageRating || 0) > 0)
        .sort((a, b) => (b.averageRating || 0) - (a.averageRating || 0));
      const top10PercentIndex = Math.max(1, Math.floor(sortedByRating.length * 0.1));
      const top10PercentThreshold = sortedByRating[top10PercentIndex]?.averageRating || sortedByRating[0]?.averageRating;

      // Filter trending dishes
      const trending = dishesData.filter(dish =>
        (dish.averageRating || 0) >= top10PercentThreshold
      );

      // Calculate restaurant stats
      const totalRatings = dishesData.reduce((sum, dish) => sum + (dish.totalRatings || 0), 0);
      const totalRating = dishesData.reduce((sum, dish) =>
        sum + ((dish.averageRating || 0) * (dish.totalRatings || 0)), 0
      );
      const averageRating = totalRatings > 0 ? totalRating / totalRatings : 0;

      // Mock restaurant data with owner info
      const restaurantData = {
        id: decodedName,
        name: decodedName,
        cuisine: dishesData[0]?.cuisine || 'Restaurant',
        address: dishesData[0]?.location || 'Address not available',
        phone: '+1 (555) 000-0000',
        rating: averageRating,
        reviews: totalRatings,
        priceRange: '$$',
        hours: '11:00 AM - 10:00 PM',
        images: dishesData.map(d => d.imageUrl).filter(Boolean),
        description: `Experience the finest dining at ${decodedName}. Our carefully crafted dishes showcase the best of culinary excellence.`,
        specialties: dishesData.slice(0, 5).map(d => d.name),
        totalDishes: dishesData.length,
        trendingDishesCount: trending.length,
        owner: {
          name: 'Chef Owner',
          photo: 'https://images.unsplash.com/photo-1759521296047-89338c8e083d?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxjaGVmJTIwcG9ydHJhaXQlMjBwcm9mZXNzaW9uYWx8ZW58MXx8fHwxNzYzOTg4NTQwfDA&ixlib=rb-4.1.0&q=80&w=1080',
          bio: `Owner and head chef at ${decodedName}, bringing years of culinary experience to every dish.`,
          story: `I started ${decodedName} with a vision to create exceptional dining experiences. Every dish tells a story, and we're passionate about sharing our culinary journey with you.`,
          favoritedish: dishesData[0]?.name || 'Chef\'s Special',
          social: {
            instagram: 'https://instagram.com',
            facebook: 'https://facebook.com',
            website: 'https://example.com',
          },
          interview: [
            {
              question: 'What inspires your cooking?',
              answer: 'I believe food is an art form that brings people together. Every ingredient tells a story, and I love creating dishes that create lasting memories.',
            },
            {
              question: 'What makes your restaurant special?',
              answer: 'We focus on quality ingredients, traditional techniques, and a warm, welcoming atmosphere. Every guest is treated like family.',
            },
          ],
        },
      };

      setRestaurant(restaurantData);
      setDishes(dishesData);
      setTrendingDishes(trending);
    } catch (error) {
      console.error('Error fetching restaurant data:', error);
      navigate('/');
    } finally {
      setLoading(false);
    }
  }, [restaurantName, navigate]);

  useEffect(() => {
    fetchRestaurantData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [restaurantName]);

  const handleDateChange = (date) => {
    setSelectedDate(date);
    setReservationData({ ...reservationData, date, time: '' });

    // Simulate checking availability
    const slots = [
      '5:00 PM', '5:30 PM', '6:00 PM', '6:30 PM',
      '7:00 PM', '7:30 PM', '8:00 PM', '8:30 PM',
      '9:00 PM', '9:30 PM'
    ];

    const availability = slots.map(time => ({
      time,
      available: Math.random() > 0.3
    }));

    setAvailableSlots(availability);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setIsSubmitted(true);
    setTimeout(() => {
      setIsSubmitted(false);
      setReservationData({
        date: '',
        time: '',
        guests: '2',
        name: '',
        email: '',
        phone: '',
        specialRequest: '',
      });
    }, 2000);
  };

  const handleVote = () => {
    fetchRestaurantData();
  };

  if (loading) {
    return (
      <div className="container mx-auto px-8 max-w-7xl py-16 text-center">
        <div className="w-1 h-20 bg-border mx-auto mb-8 animate-pulse"></div>
        <p className="text-muted-foreground">Loading restaurant profile...</p>
      </div>
    );
  }

  if (!restaurant) {
    return null;
  }

  return (
    <div>
      {/* Hero Image */}
      <div className="relative h-96 overflow-hidden">
        <ImageWithFallback
          src={restaurant.images[0] || 'https://images.unsplash.com/photo-1517248135467-4c7edcad34c4?w=800'}
          alt={restaurant.name}
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-background via-transparent to-transparent"></div>
      </div>

      <div className="container mx-auto px-8 max-w-7xl py-12">
        {/* Header */}
        <div className="mb-12">
          <div className="flex items-start justify-between mb-6">
            <div>
              <h1 className="serif text-6xl mb-3">{restaurant.name}</h1>
              <p className="text-sm text-muted-foreground uppercase tracking-widest mb-4">
                {restaurant.cuisine} · {restaurant.priceRange}
              </p>
            </div>
            <div className="flex items-center gap-2 bg-stone-100 px-4 py-2">
              <Star className="w-4 h-4 fill-current" />
              <span className="text-sm">{restaurant.rating.toFixed(1)}</span>
              <span className="text-xs text-muted-foreground">({restaurant.reviews})</span>
            </div>
          </div>

          {/* Tab Navigation */}
          <div className="flex items-center gap-8 border-b border-border mb-8">
            <button
              onClick={() => setActiveTab('overview')}
              className={`
                pb-4 text-sm uppercase tracking-widest transition-colors relative
                ${activeTab === 'overview' ? 'text-foreground' : 'text-muted-foreground hover:text-foreground'}
              `}
            >
              Overview
              {activeTab === 'overview' && (
                <motion.div
                  layoutId="restaurantTab"
                  className="absolute bottom-0 left-0 right-0 h-px bg-foreground"
                  transition={{ type: 'spring', bounce: 0, duration: 0.4 }}
                />
              )}
            </button>
            <button
              onClick={() => setActiveTab('dishes')}
              className={`
                pb-4 text-sm uppercase tracking-widest transition-colors relative
                ${activeTab === 'dishes' ? 'text-foreground' : 'text-muted-foreground hover:text-foreground'}
              `}
            >
              Dishes
              {activeTab === 'dishes' && (
                <motion.div
                  layoutId="restaurantTab"
                  className="absolute bottom-0 left-0 right-0 h-px bg-foreground"
                  transition={{ type: 'spring', bounce: 0, duration: 0.4 }}
                />
              )}
            </button>
            {restaurant.owner && (
              <button
                onClick={() => setActiveTab('owner')}
                className={`
                  pb-4 text-sm uppercase tracking-widest transition-colors relative
                  ${activeTab === 'owner' ? 'text-foreground' : 'text-muted-foreground hover:text-foreground'}
                `}
              >
                Meet the Owner
                {activeTab === 'owner' && (
                  <motion.div
                    layoutId="restaurantTab"
                    className="absolute bottom-0 left-0 right-0 h-px bg-foreground"
                    transition={{ type: 'spring', bounce: 0, duration: 0.4 }}
                  />
                )}
              </button>
            )}
          </div>

          {activeTab === 'overview' && (
            <>
              <p className="text-muted-foreground leading-relaxed max-w-3xl mb-8">
                {restaurant.description}
              </p>

              {/* Info Grid */}
              <div className="grid grid-cols-3 gap-8 pb-8 border-b border-border mb-8">
                <div className="flex items-start gap-3">
                  <MapPin className="w-5 h-5 text-muted-foreground mt-0.5" />
                  <div>
                    <p className="text-xs uppercase tracking-widest text-muted-foreground mb-1">Address</p>
                    <p className="text-sm">{restaurant.address}</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Clock className="w-5 h-5 text-muted-foreground mt-0.5" />
                  <div>
                    <p className="text-xs uppercase tracking-widest text-muted-foreground mb-1">Hours</p>
                    <p className="text-sm">{restaurant.hours}</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Phone className="w-5 h-5 text-muted-foreground mt-0.5" />
                  <div>
                    <p className="text-xs uppercase tracking-widest text-muted-foreground mb-1">Phone</p>
                    <p className="text-sm">{restaurant.phone}</p>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>

        {/* Specialties */}
        {activeTab === 'overview' && (
          <div className="mb-12">
            <h3 className="text-sm uppercase tracking-widest mb-4">Signature Dishes</h3>
            <div className="flex flex-wrap gap-2">
              {restaurant.specialties.map((specialty) => (
                <span
                  key={specialty}
                  className="px-4 py-2 border border-border text-xs"
                >
                  {specialty}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Dishes Tab */}
        {activeTab === 'dishes' && (
          <>
            {trendingDishes.length > 0 && (
              <div className="mb-16">
                <h2 className="serif text-4xl mb-4">🔥 Trending Dishes</h2>
                <p className="text-sm text-muted-foreground mb-8">
                  These dishes are in the top 10% of all rated dishes on RatedBite
                </p>
                <div className="grid grid-cols-4 gap-8">
                  {trendingDishes.map(dish => (
                    <DishCard key={dish.id} dish={dish} user={user} onVote={handleVote} />
                  ))}
                </div>
              </div>
            )}

            <div>
              <h2 className="serif text-4xl mb-8">All Dishes</h2>
              <div className="grid grid-cols-4 gap-8">
                {dishes.length === 0 ? (
                  <div className="col-span-4 text-center py-16">
                    <p className="text-muted-foreground">No dishes found for this restaurant.</p>
                  </div>
                ) : (
                  dishes.map(dish => (
                    <DishCard key={dish.id} dish={dish} user={user} onVote={handleVote} />
                  ))
                )}
              </div>
            </div>
          </>
        )}

        {/* Meet the Owner Content */}
        {activeTab === 'owner' && restaurant.owner && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="mb-12"
          >
            {/* Owner Profile */}
            <div className="grid grid-cols-12 gap-12 mb-16">
              <div className="col-span-4">
                <div className="aspect-[3/4] overflow-hidden mb-6">
                  <ImageWithFallback
                    src={restaurant.owner.photo}
                    alt={restaurant.owner.name}
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>
              <div className="col-span-8 flex flex-col justify-center">
                <div className="w-1 h-16 bg-border mb-6"></div>
                <h2 className="serif text-5xl mb-4">{restaurant.owner.name}</h2>
                <p className="text-xs uppercase tracking-widest text-muted-foreground mb-6">
                  Owner & Founder
                </p>
                <p className="text-muted-foreground leading-relaxed mb-6">
                  {restaurant.owner.bio}
                </p>

                {/* Social Links */}
                <div className="flex items-center gap-4 pt-6 border-t border-border">
                  <p className="text-xs uppercase tracking-widest text-muted-foreground">
                    Connect
                  </p>
                  <div className="flex items-center gap-3">
                    {restaurant.owner.social.instagram && (
                      <a
                        href={restaurant.owner.social.instagram}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="w-9 h-9 border border-border hover:border-foreground flex items-center justify-center transition-colors"
                      >
                        <Instagram className="w-4 h-4" />
                      </a>
                    )}
                    {restaurant.owner.social.facebook && (
                      <a
                        href={restaurant.owner.social.facebook}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="w-9 h-9 border border-border hover:border-foreground flex items-center justify-center transition-colors"
                      >
                        <Facebook className="w-4 h-4" />
                      </a>
                    )}
                    {restaurant.owner.social.website && (
                      <a
                        href={restaurant.owner.social.website}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="w-9 h-9 border border-border hover:border-foreground flex items-center justify-center transition-colors"
                      >
                        <Globe className="w-4 h-4" />
                      </a>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Owner's Story */}
            <div className="border-t border-border pt-12 mb-12">
              <h3 className="text-xs uppercase tracking-widest text-muted-foreground mb-6">
                The Story
              </h3>
              <p className="serif text-2xl leading-relaxed text-muted-foreground mb-8">
                {restaurant.owner.story}
              </p>
              <div className="bg-stone-50 p-8 border border-border">
                <p className="text-xs uppercase tracking-widest text-muted-foreground mb-3">
                  Favorite Dish
                </p>
                <p className="serif text-3xl">{restaurant.owner.favoritedish}</p>
              </div>
            </div>

            {/* Interview Q&A */}
            <div className="border-t border-border pt-12">
              <h3 className="serif text-4xl mb-8">In Conversation</h3>
              <div className="space-y-10">
                {restaurant.owner.interview.map((qa, index) => (
                  <div key={index} className="border-l-2 border-border pl-8">
                    <p className="text-xs uppercase tracking-widest text-muted-foreground mb-3">
                      Q{index + 1}
                    </p>
                    <h4 className="serif text-2xl mb-4">{qa.question}</h4>
                    <p className="text-muted-foreground leading-relaxed">
                      {qa.answer}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </motion.div>
        )}

        {/* Gallery */}
        {activeTab === 'overview' && restaurant.images.length > 1 && (
          <div className="grid grid-cols-3 gap-4 mb-12">
            {restaurant.images.slice(1, 4).map((image, index) => (
              <div key={index} className="aspect-[4/3] overflow-hidden">
                <ImageWithFallback
                  src={image}
                  alt={`${restaurant.name} ${index + 1}`}
                  className="w-full h-full object-cover hover:scale-105 transition-transform duration-700"
                />
              </div>
            ))}
          </div>
        )}

        {/* Reservation Form */}
        {activeTab === 'overview' && (
          <div className="border-t border-border pt-12">
            <h2 className="serif text-4xl mb-2">Make a Reservation</h2>
            <p className="text-sm text-muted-foreground mb-8">
              Reserve your table and we'll confirm your booking shortly
            </p>

            {!isSubmitted ? (
              <form onSubmit={handleSubmit}>
                <div className="grid grid-cols-3 gap-6 mb-6">
                  {/* Date */}
                  <div className="col-span-1">
                    <label className="text-xs uppercase tracking-widest mb-3 block">
                      Date
                    </label>
                    <div className="relative">
                      <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                      <Input
                        type="date"
                        required
                        value={reservationData.date}
                        onChange={(e) => handleDateChange(e.target.value)}
                        className="pl-10 h-12 border-border"
                      />
                    </div>
                  </div>

                  {/* Guests */}
                  <div className="col-span-1">
                    <label className="text-xs uppercase tracking-widest mb-3 block">
                      Guests
                    </label>
                    <div className="relative">
                      <Users className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                      <select
                        required
                        value={reservationData.guests}
                        onChange={(e) => setReservationData({ ...reservationData, guests: e.target.value })}
                        className="w-full h-12 pl-10 pr-4 border border-border bg-background text-sm appearance-none cursor-pointer"
                      >
                        {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((num) => (
                          <option key={num} value={num}>
                            {num} {num === 1 ? 'Guest' : 'Guests'}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>

                {/* Available Time Slots */}
                {availableSlots.length > 0 && (
                  <div className="mb-8">
                    <label className="text-xs uppercase tracking-widest mb-4 block">
                      Available Times
                    </label>
                    <div className="grid grid-cols-5 gap-3">
                      {availableSlots.map((slot) => (
                        <button
                          key={slot.time}
                          type="button"
                          disabled={!slot.available}
                          onClick={() => setReservationData({ ...reservationData, time: slot.time })}
                          className={`
                            h-12 border text-sm transition-all
                            ${!slot.available
                              ? 'border-border bg-stone-50 text-muted-foreground cursor-not-allowed line-through'
                              : reservationData.time === slot.time
                                ? 'border-foreground bg-foreground text-background'
                                : 'border-border hover:border-foreground'
                            }
                          `}
                        >
                          {slot.time}
                        </button>
                      ))}
                    </div>
                    {selectedDate && (
                      <p className="text-xs text-muted-foreground mt-3">
                        {availableSlots.filter(s => s.available).length} of {availableSlots.length} slots available
                      </p>
                    )}
                  </div>
                )}

                <div className="grid grid-cols-2 gap-6 mb-6">
                  {/* Name */}
                  <div>
                    <label className="text-xs uppercase tracking-widest mb-3 block">
                      Full Name
                    </label>
                    <Input
                      type="text"
                      required
                      placeholder="John Doe"
                      value={reservationData.name}
                      onChange={(e) => setReservationData({ ...reservationData, name: e.target.value })}
                      className="h-12 border-border"
                    />
                  </div>

                  {/* Email */}
                  <div>
                    <label className="text-xs uppercase tracking-widest mb-3 block">
                      Email Address
                    </label>
                    <Input
                      type="email"
                      required
                      placeholder="john@example.com"
                      value={reservationData.email}
                      onChange={(e) => setReservationData({ ...reservationData, email: e.target.value })}
                      className="h-12 border-border"
                    />
                  </div>
                </div>

                <div className="mb-6">
                  <label className="text-xs uppercase tracking-widest mb-3 block">
                    Phone Number
                  </label>
                  <Input
                    type="tel"
                    required
                    placeholder="+1 (555) 000-0000"
                    value={reservationData.phone}
                    onChange={(e) => setReservationData({ ...reservationData, phone: e.target.value })}
                    className="h-12 border-border"
                  />
                </div>

                <div className="mb-8">
                  <label className="text-xs uppercase tracking-widest mb-3 block">
                    Special Requests (Optional)
                  </label>
                  <textarea
                    placeholder="Dietary restrictions, special occasions, seating preferences..."
                    value={reservationData.specialRequest}
                    onChange={(e) => setReservationData({ ...reservationData, specialRequest: e.target.value })}
                    className="w-full min-h-24 px-4 py-3 border border-border bg-background text-sm resize-none"
                  />
                </div>

                <button
                  type="submit"
                  className="w-full h-14 bg-foreground text-background hover:bg-foreground/90 transition-colors text-sm uppercase tracking-widest"
                >
                  Confirm Reservation
                </button>
              </form>
            ) : (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="text-center py-12"
              >
                <div className="w-16 h-16 rounded-full border-2 border-foreground flex items-center justify-center mx-auto mb-6">
                  <motion.div
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ delay: 0.2 }}
                  >
                    ✓
                  </motion.div>
                </div>
                <h3 className="serif text-3xl mb-3">Reservation Confirmed</h3>
                <p className="text-sm text-muted-foreground">
                  We've sent a confirmation email to {reservationData.email}
                </p>
              </motion.div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default RestaurantProfile;
